import React, { useState, useCallback, useEffect } from "react";
import ConvoAIPanel, { ConvoAIMessage } from "./convoai-hub";
import { CTAButton, ExtendedTemplate, TemplateVariables } from "../../../views/marketing-pages/create-campaign/types";
import { PhoneMockup } from "./phone-mock";
import { MessagingProps, VARIABLE_COLORS } from "../../../views/marketing-pages/create-campaign/data";
import useRequest from "../../hook/use-req";
import useWhatsappAccounts from "../../hook/use-whatsapp-accounts";
import { CampaignContent } from "../../types";
import { SingleCampaignResponse } from "../../../views/marketing-pages/campaign/types";
import { ExternalLink, Phone, Plus, Reply } from "lucide-react";
import { TemplateCreator } from "../templates/create-template";
import { TemplateModule } from "../../../views/marketing-pages/templates";
import { WhatsappMockUp } from "./whatsapp-mock";
import ScrollToTop from "../../../routes/scroll-top";
import SwitchIcon from "../../../assets/images/switch.png";
import { applySavedAudienceHeaders } from "./audience-headers";

// ─────────────────────────────────────────────────────────────
// VARIABLE MAPPING TYPES & HELPERS
// ─────────────────────────────────────────────────────────────

type VariableSource = "csv" | "static";

interface VariableMapping {
  variableName: string;
  source: VariableSource;
  csvColumn?: string;
  staticValue?: string;
}

function extractVariableNames(templateContent: string): string[] {
  const matches = [...templateContent.matchAll(/{{(\w+)}}/g)];
  const unique = new Map<string, true>();
  matches.forEach((m) => unique.set(m[1], true));
  return [...unique.keys()];
}

function buildInitialMappings(
  variableNames: string[],
  csvColumns: string[],
  existingColumnMap?: Record<string, string>,
  existingStatic?: Record<string, string>
): VariableMapping[] {
  return variableNames.map((name) => {
    const cleanName = name.replace(/[{}]/g, "");

    if (existingColumnMap?.[cleanName] || existingColumnMap?.[name]) {
      return {
        variableName: cleanName,
        source: "csv",
        csvColumn: existingColumnMap[cleanName] || existingColumnMap[name]
      };
    }
    if (existingStatic?.[cleanName] || existingStatic?.[name]) {
      return {
        variableName: cleanName,
        source: "static",
        staticValue: existingStatic[cleanName] || existingStatic[name]
      };
    }

    const autoMatch = csvColumns.find((col) =>
      col.toLowerCase() === cleanName.toLowerCase() ||
      col.toLowerCase().replace(/[_\s]/g, "") === cleanName.toLowerCase().replace(/[_\s]/g, "")
    );

    if (autoMatch && csvColumns.length > 0) {
      return { variableName: cleanName, source: "csv", csvColumn: autoMatch };
    }

    return { variableName: cleanName, source: "static", staticValue: "" };
  });
}

function mappingsToPayload(mappings: VariableMapping[]): {
  variable_column_map: Record<string, string>;
  variables: Record<string, string>;
  data_to_personalize: string[];
} {
  const variable_column_map: Record<string, string> = {};
  const variables: Record<string, string> = {};
  const data_to_personalize: string[] = [];

  mappings.forEach((m) => {
    if (m.source === "csv" && m.csvColumn) {
      variable_column_map[m.variableName] = m.csvColumn;
      data_to_personalize.push(m.variableName);
    } else {
      variables[m.variableName] = m.staticValue || "";
    }
  });

  return { variable_column_map, variables, data_to_personalize };
}
// ─────────────────────────────────────────────────────────────
// OTHER HELPERS
// ─────────────────────────────────────────────────────────────

const normalizeTemplateButtons = (buttons: any): any[] => {
  if (!buttons) return [];
  if (Array.isArray(buttons)) return buttons;
  if (Array.isArray(buttons.buttons)) return buttons.buttons;
  return [];
};
const getButtonText = (button: any) =>
  typeof button === "string" ? button : button?.text || button?.label || "";

const getButtonUrl = (button: any) =>
  typeof button === "string" ? "" : button?.url || button?.website_url || button?.value || "";

// Only a URL-type button can meaningfully take the CTA form's url override —
// a quick-reply button has nowhere to navigate to, and WhatsApp doesn't
// support adding one. Used both to decide whether the CTA form shows a URL
// field at all, and to gate the overlay below so a stale/irrelevant url can
// never attach itself to a quick-reply button.
const isUrlButtonType = (button: any) => button?.type === "URL" || !!getButtonUrl(button);

// Applies the CTA form's live edits onto the template's own first button
// (when it's a URL button) instead of replacing the whole array — a
// template can have 2+ buttons, and only the first one is ever editable
// here, so every other button must still make it through untouched.
const applyCtaOverride = (templateButtons: any[], cta: { label: string; url: string }): any[] => {
  if (templateButtons.length === 0) {
    return cta.label && cta.url ? [{ type: "URL", text: cta.label, url: cta.url }] : [];
  }
  return templateButtons.map((btn, i) =>
    i === 0 && isUrlButtonType(btn) && cta.label
      ? { ...btn, text: cta.label, url: cta.url || getButtonUrl(btn) }
      : btn,
  );
};

const convertToFrontendTemplate = (backendTemplate: TemplateModule): ExtendedTemplate => {
  // Any real file header — not just image — needs the media upload step and
  // the "media required" validation gate; only "text"/"none"/null don't.
  let type: "Text" | "Media + Text" = "Text";
  if (["image", "video", "document", "file"].includes(backendTemplate.header_type || "")) {
    type = "Media + Text";
  }

  const preview = backendTemplate.content.replace(/{{(\d+)}}/g, (match, index) => {
    const variable = backendTemplate.variables.find((v) => v.index === parseInt(index));
    return variable?.example || match;
  });

  return {
    id: backendTemplate.id,
    name: backendTemplate.template_name,
    body: backendTemplate.content,
    snapshot_url: backendTemplate.snapshot_url || "",
    type,
    preview,
    originalContent: backendTemplate.content,
    variables: backendTemplate.variables,
    header_type: backendTemplate.header_type || null,
    header_content: backendTemplate.header_content || null,
    footer: backendTemplate.footer || null,
    buttons: backendTemplate.buttons || null,
    language: backendTemplate.language,
    category: backendTemplate.category,
    wa_status: backendTemplate.wa_status,
    waba_id: backendTemplate.waba_id,
  };
};

// ─────────────────────────────────────────────────────────────
// 1. TemplateListItem
// ─────────────────────────────────────────────────────────────

interface TemplateListItemProps {
  template: ExtendedTemplate;
  selected: boolean;
  onSelect: (t: ExtendedTemplate) => void;
}

function TemplateListItem({ template, selected, onSelect }: TemplateListItemProps) {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "TRUE":
        return "bg-gray-200 text-gray-700";
      case "PENDING":
        return "bg-yellow-100 text-yellow-700";
      case "REJECTED":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <button
      onClick={() => onSelect(template)}
      className={`w-full text-left px-4 py-3 border-b border-slate-100 dark:border-slate-800 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800
        ${selected ? "bg-indigo-50 border-l-4 border-l-indigo-500" : "border-l-4 border-l-transparent"}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="font-semibold text-slate-800 dark:text-slate-200 text-sm truncate">{template.name}</span>
        <span
          className={`text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${getStatusBadge(template.wa_status)}`}
        >
          {template.wa_status}
        </span>
      </div>
      <p className="text-xs text-slate-400 dark:text-slate-500 truncate leading-relaxed">{template.preview}</p>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// 2. TemplateSidebar
// ─────────────────────────────────────────────────────────────

interface TemplateSidebarProps {
  templates: ExtendedTemplate[];
  wabaAccounts?: Array<{ value: string; label: string }>;
  selected: ExtendedTemplate | null;
  onSelect: (t: ExtendedTemplate) => void;
  loading?: boolean;
}

function TemplateSidebar({ templates, selected, onSelect, loading }: TemplateSidebarProps) {
  const [query, setQuery] = useState("");
  const filtered = templates.filter((t) => t.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="flex flex-col h-full border-r border-slate-200 dark:border-slate-700">
      <div className="px-4 pt-5 pb-3">
        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Choose Your Template</h1>
        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Pick a message template to edit</p>
      </div>
      <div className="px-4 bg-[#EAEDFB] p-3">
        <div className="relative">
          <input
            type="text"
            placeholder="Search Templates"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full border border-slate-200 dark:border-slate-700 rounded-lg pl-3 pr-9 py-2 text-sm placeholder:text-slate-400 dark:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white dark:bg-gray-900"
          />
          <svg className="absolute right-3 top-2.5 text-slate-400 dark:text-slate-500 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
        </div>
      </div>
      <div className="flex-1 bg-white dark:bg-gray-900 overflow-y-auto" style={{ maxHeight: "650px", minHeight: "300px" }}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-slate-400 dark:text-slate-500 text-sm py-8">
            {templates.length === 0 ? "No templates found" : "No matching templates"}
          </p>
        ) : (
          filtered.map((t) => (
            <TemplateListItem key={t.id} template={t} selected={selected?.id === t.id} onSelect={onSelect} />
          ))
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 3. MediaUpload
// ─────────────────────────────────────────────────────────────

interface MediaUploadProps {
  onUpload?: (file: File) => void;
  previewUrl?: string | null;
  uploadedFile?: File | null;
  headerContent?: string | null;
  headerType?: string | null;
  onReplace?: () => void;
  onRemove?: () => void;
}

// The template's own header_type tells us exactly which kind of file this
// send needs — image, video, or document — so the uploader only accepts
// (and only prompts for) that one kind, instead of a generic "image or
// video" regardless of what the template actually declares.
const acceptForHeaderKind = (kind: "image" | "video" | "document") =>
  kind === "image" ? "image/*" : kind === "video" ? "video/*" : ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx";

const matchesHeaderKind = (file: File, kind: "image" | "video" | "document") =>
  kind === "image" ? file.type.startsWith("image/")
    : kind === "video" ? file.type.startsWith("video/")
    : !file.type.startsWith("image/") && !file.type.startsWith("video/");

function MediaUpload({ onUpload, previewUrl: externalPreviewUrl, uploadedFile, headerType, onRemove }: MediaUploadProps) {
  const [dragging, setDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(externalPreviewUrl || null);
  const headerKind: "image" | "video" | "document" =
    headerType === "video" ? "video" : headerType === "document" || headerType === "file" ? "document" : "image";

  useEffect(() => {
    setPreviewUrl(externalPreviewUrl || null);
  }, [externalPreviewUrl]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const file = e.dataTransfer.files[0];
      if (file && matchesHeaderKind(file, headerKind)) {
        onUpload?.(file);
      }
    },
    [onUpload, headerKind]
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && matchesHeaderKind(file, headerKind)) {
      onUpload?.(file);
    }
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    onRemove?.();
    const fileInput = document.getElementById("media-file-input") as HTMLInputElement;
    if (fileInput) fileInput.value = "";
  };

  const isVideo = (url: string) => headerKind === "video" || url.match(/\.(mp4|webm|ogg)$/i) || uploadedFile?.type.startsWith("video/");

  const kindLabel = headerKind === "image" ? "image" : headerKind === "video" ? "video" : "document";

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-4 text-center transition-colors
          ${dragging ? "border-indigo-400 bg-[#F3F8F6] dark:bg-indigo-500/10" : "border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50"}`}
      >
        {previewUrl ? (
          <div className="space-y-3">
            <div className="relative rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 max-h-48 flex items-center justify-center">
              {headerKind === "document" ? (
                <div className="flex flex-col items-center justify-center gap-2 py-8 text-slate-400 dark:text-slate-500">
                  <svg className="w-10 h-10" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14 2v6h6" />
                  </svg>
                </div>
              ) : isVideo(previewUrl) ? (
                <video src={previewUrl} className="max-w-full max-h-48 object-contain" controls controlsList="nodownload" />
              ) : (
                <img src={previewUrl} alt="Preview" className="max-w-full max-h-48 object-contain" />
              )}
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="truncate max-w-[200px]">{uploadedFile ? uploadedFile.name : "Media file"}</span>
              </div>
              <div className="flex gap-2">
                <label className="cursor-pointer">
                  <input id="media-file-input" type="file" className="hidden" accept={acceptForHeaderKind(headerKind)} onChange={handleFileSelect} />
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors shadow-sm">
                    Replace
                  </span>
                </label>
                <button onClick={handleRemove} className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg text-xs font-medium text-red-600 hover:bg-red-100 transition-colors">
                  Remove
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <p className="text-[#0F766E] text-sm font-medium mb-3">Upload header {kindLabel}</p>
            <label className="inline-block cursor-pointer">
              <input type="file" className="hidden" accept={acceptForHeaderKind(headerKind)} onChange={handleFileSelect} />
              <span className="px-4 py-2 bg-white dark:bg-gray-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors shadow-sm">
                Browse Files
              </span>
            </label>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-3">
              {headerKind === "image" ? "Supports JPG, PNG • max 16MB"
                : headerKind === "video" ? "Supports MP4, WEBM • max 16MB"
                : "Supports PDF, DOC, XLS, PPT • max 100MB"}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 4. MessageBodyPreview - FIXED
// ─────────────────────────────────────────────────────────────

function MessageBodyPreview({
  template,
  mappings,
}: {
  template: ExtendedTemplate;
  mappings: VariableMapping[];
}) {
  const content = template.originalContent || "";
  const parts = content.split(/({{\w+}})/g);

  const savedHeadersStr = localStorage.getItem("csv_headers");
  const csvColumns: string[] = savedHeadersStr ? JSON.parse(savedHeadersStr) : [];
  const savedRowStr = localStorage.getItem("first_row_values");
  const csvColumnsRow: (string | number | null)[] = savedRowStr ? JSON.parse(savedRowStr) : [];

  return (
    <div className="mb-5">
      <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Message Body</p>
      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-sm text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
        {parts.map((part: string, i: number) => {
          const match = part.match(/{{(\w+)}}/);
          if (match) {
            const varName = match[1];
            const mapping = mappings.find((m) =>
              m.variableName === varName || m.variableName === String(varName)
            );

            let displayValue = part;
            if (mapping) {
              if (mapping.source === "static" && mapping.staticValue) {
                displayValue = mapping.staticValue;
              } else if (mapping.csvColumn) {
                const colIndex = csvColumns.indexOf(mapping.csvColumn);
                const rowValue = colIndex !== -1 ? csvColumnsRow[colIndex] : undefined;
                displayValue = rowValue !== undefined && rowValue !== null ? String(rowValue) : part;
              }
            }

            const colorIdx = mappings.findIndex((m) => m.variableName === varName || m.variableName === String(varName));
            const color = VARIABLE_COLORS[colorIdx % VARIABLE_COLORS.length] || "bg-indigo-100 text-indigo-700";

            return (
              <span key={i} className={`inline-block px-1.5 py-0.5 rounded text-xs font-semibold mx-0.5 ${color}`}>
                {displayValue}
              </span>
            );
          }
          return <span key={i}>{part}</span>;
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 5. VariableMappingForm
// ─────────────────────────────────────────────────────────────

interface VariableMappingFormProps {
  variableNames: string[];
  csvColumns: string[];           // from savedHeaders
  csvPreviewRow?: Record<string, string>;
  mappings: VariableMapping[];
  onChange: (mappings: VariableMapping[]) => void;
  uploadedFileName?: string;
}

interface VariableMappingFormProps {
  variableNames: string[];
  mappings: VariableMapping[];
  onChange: (mappings: VariableMapping[]) => void;
  uploadedFileName?: string;
}
interface Metadata {
  unique_file_name: string;
  // add other fields if they exist
}
function VariableMappingForm({
  variableNames,
  mappings,
  onChange,
  uploadedFileName,
}: VariableMappingFormProps) {
  if (variableNames.length === 0) return null;

  // Get headers ONLY from localStorage
  const savedHeadersStr = localStorage.getItem("csv_headers");
  const csvColumns: string[] = savedHeadersStr ? JSON.parse(savedHeadersStr) : [];
  const savedHeadersColumn = localStorage.getItem("first_row_values");
  const csvColumnsRow: (string | number | null)[] = savedHeadersColumn ? JSON.parse(savedHeadersColumn) : [];
  const csvHeadersName = localStorage.getItem("metadata");

  const metadata: Metadata | null = csvHeadersName
    ? JSON.parse(csvHeadersName)
    : null;


  const mappedCount = mappings.filter(
    (m) => (m.source === "csv" && m.csvColumn) || (m.source === "static" && m.staticValue)
  ).length;

  const updateMapping = (index: number, patch: Partial<VariableMapping>) => {
    onChange(mappings.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  };

  const getPreview = (m: VariableMapping): string => {
    if (m.source === "csv" && m.csvColumn) {
      const colIndex = csvColumns.indexOf(m.csvColumn);
      const rowValue = colIndex !== -1 ? csvColumnsRow[colIndex] : undefined;
      return rowValue !== undefined && rowValue !== null ? String(rowValue) : m.csvColumn;
    }
    return m.staticValue || "";
  };

  return (
    <div className="space-y-6">
      {/* Uploaded File Preview */}
      {uploadedFileName && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center gap-3">
          <div className="w-8 h-8 bg-white dark:bg-gray-900 rounded-xl flex items-center justify-center border border-emerald-200">
            📄
          </div>
          <div>
            <p className="font-medium text-emerald-700">{uploadedFileName}</p>
            <p className="text-xs text-emerald-600">{csvColumns.length} columns detected</p>
          </div>
        </div>
      )}

      {/* Mapping Table */}
      <div>
        <p className="text-sm font-semibold text-[#4338CA] underline">{metadata?.unique_file_name}</p>
        <div className="flex justify-between items-center mb-4">

          <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">List of message body variables</p>
          <span className="text-xs font-medium px-3 py-1 bg-white dark:bg-gray-900 border border-slate-200 dark:border-slate-700 rounded-full">
            {mappedCount}/{variableNames.length} Mapped
          </span>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-[140px_1fr_140px] gap-4 px-6 py-3 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            <div>Variables</div>
            <div>Map Data Source</div>
            <div>Column Preview</div>
          </div>

          {mappings.map((m, idx) => (
            <div key={m.variableName} className="grid grid-cols-[140px_1fr_140px] gap-4 px-6 py-5 border-b last:border-b-0 items-center">
              {/* Variable Name */}
              <div className="font-mono text-sm text-indigo-600">
                {`{{${m.source === "csv" && m.csvColumn ? m.csvColumn : m.variableName}}}`}
              </div>

              {/* Data Source */}
              <div className="space-y-3">
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={m.source === "csv"}
                      onChange={() => updateMapping(idx, { source: "csv", staticValue: undefined })}
                      className="accent-emerald-600"
                    />
                    <span className="text-sm">CSV Column</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={m.source === "static"}
                      onChange={() => updateMapping(idx, { source: "static", csvColumn: undefined })}
                      className="accent-emerald-600"
                    />
                    <span className="text-sm">Static Value</span>
                  </label>
                </div>

                {m.source === "csv" ? (
                  <select
                    value={m.csvColumn || ""}
                    onChange={(e) => updateMapping(idx, { csvColumn: e.target.value })}
                    className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
                  >
                    <option value="">Select column...</option>
                    {csvColumns.map((col) => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    value={m.staticValue || ""}
                    onChange={(e) => updateMapping(idx, { staticValue: e.target.value })}
                    placeholder="Enter static value"
                    className="w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2.5 text-sm"
                  />
                )}
              </div>

              {/* Preview */}
              <div>
                {getPreview(m) ? (
                  <span className="inline-block bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1.5 rounded-lg text-sm">
                    {getPreview(m)}
                  </span>
                ) : (
                  <span className="text-slate-300 dark:text-slate-600 italic">—</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Wizard footer — Back / Preview and Launch. Rendered after CTAButtonForm
// so it's always the last thing in the panel, regardless of whether the
// template has variables to map or a CTA button to configure.
// ─────────────────────────────────────────────────────────────
function WizardFooterActions({ onBack, onLaunch }: { onBack: () => void; onLaunch: () => void }) {
  return (
    <div className="px-6 py-4 border-t top-7 border-slate-100 dark:border-slate-800 flex justify-end gap-3 shrink-0">
      <button onClick={onBack} className="px-5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
        Back
      </button>
      <button onClick={onLaunch} className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-sm transition-colors">
        Preview and Launch
      </button>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────
// 6. CTAButtonForm
// ─────────────────────────────────────────────────────────────

interface CTAButtonFormProps {
  cta: CTAButton;
  onChange: (c: CTAButton) => void;
  templateButtons?: any;
}

function CTAButtonForm({ cta, onChange, templateButtons }: CTAButtonFormProps) {
  const buttons = normalizeTemplateButtons(templateButtons);
  if (buttons.length === 0) return null;

  // Button text comes from the approved template and can't be changed here —
  // every button is listed read-only, same order as the live preview. Only
  // the first button's URL (when it's a URL button) stays editable, since
  // that's the one applyCtaOverride carries through to the payload.
  return (
    <div className="mt-6">
      <p className="text-xs font-semibold text-indigo-500 uppercase tracking-wider mb-3">CTA {buttons.length > 1 ? "Buttons" : "Button"}</p>
      <div className="space-y-2">
        {buttons.map((btn, i) => {
          const isUrl = isUrlButtonType(btn);
          const isPhone = btn?.type === "PHONE_NUMBER";
          const Icon = isUrl ? ExternalLink : isPhone ? Phone : Reply;
          return (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-2 w-60 max-w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 cursor-default">
                <Icon size={14} className="shrink-0 text-emerald-600" />
                <span className="truncate">{getButtonText(btn) || "—"}</span>
              </div>
              {isUrl &&
                (i === 0 ? (
                  <input
                    value={cta.url}
                    onChange={(e) => onChange({ ...cta, url: e.target.value })}
                    placeholder="Button URL"
                    className="w-72 max-w-full border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                  />
                ) : (
                  <span className="text-xs text-slate-400 dark:text-slate-500 truncate max-w-xs">{getButtonUrl(btn)}</span>
                ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 7. RightPanel
// ─────────────────────────────────────────────────────────────

function RightPanel({
  template, vars, cta, mediaFile, senderName,
  isConvoOpen, onToggleConvo, onConvoSend,
}: {
  template: ExtendedTemplate | null;
  senderName?: string;
  vars: TemplateVariables;
  cta: CTAButton;
  mediaFile: File | null;
  isConvoOpen: boolean;
  onToggleConvo: () => void;
  onConvoSend: (message: string, history: ConvoAIMessage[]) => Promise<string>;
}) {
  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 border-l border-slate-200 dark:border-slate-700">
      <div className="px-5 pt-4 pb-3 flex items-center justify-between bg-[#F7FCF3] border-b border-slate-100 dark:border-slate-800 shrink-0">
        <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Live Preview</span>
      </div>
      <div className="flex-1 relative overflow-hidden">
        <div className={`absolute inset-0 flex flex-col items-center justify-start pt-6 pb-4 px-4 overflow-y-auto transition-opacity duration-200 ${isConvoOpen ? "opacity-0 pointer-events-none" : "opacity-100"}`}>
          {template && <WhatsappMockUp template={template} senderName={senderName} vars={vars} cta={cta} mediaFile={mediaFile} />}
          <div className="flex justify-end items-end w-full">
            <button onClick={onToggleConvo} title={isConvoOpen ? "Back to preview" : "Open ConvoAI"} className={`absolute bottom-6 right-6 z-10 flex items-center justify-center transition-all duration-200 ${isConvoOpen ? "text-indigo-600 ring-2 ring-indigo-300" : "text-white hover:scale-110"}`}>
              <img src={SwitchIcon} alt="SwitchIcon" />
            </button>
          </div>
        </div>
        <div className={`absolute inset-0 transition-opacity duration-200 ${isConvoOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
          <ConvoAIPanel isOpen={isConvoOpen} onClose={onToggleConvo} onSend={onConvoSend} />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 8. TemplateEditor
// ─────────────────────────────────────────────────────────────

interface TemplateEditorProps {
  template: ExtendedTemplate;
  uploadedFileName?: string;
  mappings: VariableMapping[];
  onMappingsChange: (m: VariableMapping[]) => void;
  csvColumns: string[];
  csvPreviewRow?: Record<string, string>;
  cta: CTAButton;
  onCTAChange: (c: CTAButton) => void;
  onBack: () => void;
  onLaunch: () => void;
  onMediaUpload: (f: File) => void;
  onMediaRemove?: () => void;
  mediaPreviewUrl?: string | null;
  mediaFile: File | null;
  backendTemplates: TemplateModule[];
  onTemplateCreated: () => void;
  wabaAccounts?: Array<{ value: string; label: string }>;
}

function TemplateEditor({
  template, mappings, onMappingsChange, csvColumns, csvPreviewRow,
  cta, onCTAChange, onBack, onLaunch,
  onMediaUpload, onMediaRemove, mediaFile, mediaPreviewUrl,
  backendTemplates, onTemplateCreated, wabaAccounts,
  uploadedFileName,
}: TemplateEditorProps) {
  const [showCreator, setShowCreator] = React.useState(false);

  const variableNames = extractVariableNames(template.originalContent);

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between">
        <div className="px-6 pt-5 pb-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
          <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">{template.name}</h2>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Fill variables to personalise your message</p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreator(true)}
          className="flex items-center h-10 gap-1.5 px-4 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors bg-white dark:bg-gray-900 shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Create New Template
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
        {template.type === "Media + Text" && (
          <MediaUpload
            onUpload={onMediaUpload}
            previewUrl={mediaPreviewUrl}
            uploadedFile={mediaFile}
            headerContent={template.header_content}
            headerType={template.header_type}
            onRemove={onMediaRemove}
          />
        )}

        <MessageBodyPreview template={template} mappings={mappings} />

        <VariableMappingForm
          variableNames={variableNames}
          csvColumns={csvColumns}
          csvPreviewRow={csvPreviewRow}
          mappings={mappings}
          onChange={onMappingsChange}
          uploadedFileName={uploadedFileName}
        />

        {normalizeTemplateButtons(template.buttons).length > 0 && (
          <CTAButtonForm templateButtons={template.buttons} cta={cta} onChange={onCTAChange} />
        )}

        <WizardFooterActions onBack={onBack} onLaunch={onLaunch} />
      </div>


      <TemplateCreator
        isOpen={showCreator}
        templates={backendTemplates}
        wabaAccounts={wabaAccounts}
        onClose={() => setShowCreator(false)}
        onSaveDraft={() => { setShowCreator(false); onTemplateCreated(); }}
        onSendRequest={() => { setShowCreator(false); onTemplateCreated(); }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 9. LaunchModal
// ─────────────────────────────────────────────────────────────

interface LaunchModalProps {
  template: ExtendedTemplate;
  vars: TemplateVariables;
  cta: CTAButton;
  mediaFile: File | null;
  onClose: () => void;
  onConfirm: () => void;
}

function LaunchModal({ template, vars, cta, mediaFile, onClose, onConfirm }: LaunchModalProps) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="px-6 pt-5 pb-3 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
          <h3 className="font-bold text-slate-900 dark:text-slate-100">Preview &amp; Launch</h3>
          <button onClick={onClose} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 text-xl leading-none">×</button>
        </div>
        <div className="py-6 flex justify-center bg-slate-50 dark:bg-slate-800">
          <PhoneMockup template={template} vars={vars} cta={cta} mediaFile={mediaFile} />
        </div>
        <div className="px-6 pb-5 pt-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800">
            Edit
          </button>
          <button onClick={onConfirm} className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-sm">
            🚀 Launch Campaign
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 10. Main Messaging Component
// ─────────────────────────────────────────────────────────────

export default function Messaging({
  campaignData,
  setCampaignData,
  onValidation,
  onSubmit,
  onBack,
  singleCampaign,
  csvColumns = [],
  csvPreviewRow = {},
  uploadedFileName,
}: MessagingProps & {
  singleCampaign?: SingleCampaignResponse | null;
  csvColumns?: string[];
  csvPreviewRow?: Record<string, string>;
  uploadedFileName?: string;
}) {
  const token = localStorage.getItem("authToken");
  const [backendTemplates, setBackendTemplates] = useState<TemplateModule[]>([]);
  const [frontendTemplates, setFrontendTemplates] = useState<ExtendedTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<ExtendedTemplate | null>(null);
  const [variableMappings, setVariableMappings] = useState<VariableMapping[]>([]);
  const [isConvoOpen, setIsConvoOpen] = useState(false);
  const [showLaunch, setShowLaunch] = useState(false);
  const [, setMobileSidebarOpen] = useState(false);
  const [mobileTab, setMobileTab] = useState<"editor" | "preview">("editor");
  const { wabaAccounts } = useWhatsappAccounts();

  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState<string | null>(null);

  const { makeRequest: getTemplates, loading: templateLoader } = useRequest("/templates/", "GET", {
    Authorization: `Bearer ${token}`,
  });

  const fetchTemplates = async () => {
    const [response] = await getTemplates(undefined, { channel: "WhatsApp", template_category: "marketing" });
    const raw = response?.data ?? (Array.isArray(response) ? response : []);
    const approved = raw.filter((t: TemplateModule) => t?.wa_status === "APPROVED");

    setBackendTemplates(approved);
    setFrontendTemplates(approved.map(convertToFrontendTemplate));
  };

  useEffect(() => { fetchTemplates(); }, []);

  // Editing a draft built on a saved audience can open straight on this step,
  // before the setup step has had a chance to write that audience's
  // csv_headers (see audience-headers.ts) — without them every CSV Column
  // dropdown is empty and the saved variable_column_map has nothing to show
  // against. Fetch them once here in that case; the bump re-renders the
  // dropdowns, which read localStorage at render time.
  const { makeRequest: fetchAudienceList } = useRequest("/audience/list", "GET", { Authorization: `Bearer ${token}` });
  const [, setHeadersLoaded] = useState(0);
  useEffect(() => {
    const ids = singleCampaign?.audiences;
    if (!ids?.length || localStorage.getItem("csv_headers")) return;
    let cancelled = false;
    (async () => {
      const [response] = await fetchAudienceList();
      if (cancelled || !Array.isArray(response?.audiences)) return;
      if (applySavedAudienceHeaders(response.audiences.filter((a: any) => ids.includes(a.audience_id)))) setHeadersLoaded((n) => n + 1);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleCampaign?.audiences]);

  // Restore media preview
  useEffect(() => {
    if (campaignData.mediaFileUrl && !campaignData.mediaFileUrl.startsWith("blob:")) {
      setMediaPreviewUrl(campaignData.mediaFileUrl);
      return;
    }
    if (campaignData.mediaFile) {
      setMediaPreviewUrl(URL.createObjectURL(campaignData.mediaFile));
      return;
    }
    if (campaignData.content?.header_value) {
      setMediaPreviewUrl(campaignData.content.header_value);
    }
  }, [campaignData]);

  useEffect(() => {
    if (campaignData.mediaFile) setMediaFile(campaignData.mediaFile);
  }, [campaignData.mediaFile]);

  // Select template
  useEffect(() => {
    if (frontendTemplates.length > 0 && !selectedTemplate) {
      const fromSingle = singleCampaign?.content?.template_id
        ? frontendTemplates.find((t) => t.id === singleCampaign.content.template_id)
        : null;
      const fromData = campaignData.content?.template_id
        ? frontendTemplates.find((t) => t.id === campaignData.content?.template_id)
        : null;
      setSelectedTemplate(fromSingle || fromData || frontendTemplates[0]);
    }
  }, [frontendTemplates, singleCampaign, campaignData.content?.template_id]);

  // Rebuild mappings
  useEffect(() => {
    if (!selectedTemplate) return;
    const names = extractVariableNames(selectedTemplate.originalContent);

    const draftColumnMap = (singleCampaign?.content as any)?.variable_column_map as Record<string, string> | undefined;
    const draftStatic = (singleCampaign?.content as any)?.variables as Record<string, string> | undefined;

    const built = buildInitialMappings(names, csvColumns, draftColumnMap, draftStatic);
    setVariableMappings(built);
  }, [selectedTemplate]);

  // CTA
  const [cta, setCTA] = useState<CTAButton>(() => {
    if (selectedTemplate) {
      const firstButton = normalizeTemplateButtons(selectedTemplate?.buttons)[0];
      if (firstButton) return { label: getButtonText(firstButton), url: getButtonUrl(firstButton) };
    }
    const buttons = singleCampaign?.content?.buttons;
    if (buttons?.length) return { label: buttons[0].text || "", url: buttons[0].url || "" };
    const campaignButtons = campaignData.content?.buttons;
    if (campaignButtons?.length) return { label: campaignButtons[0].text || "", url: campaignButtons[0].url || "" };
    return { label: "", url: "" };
  });

  useEffect(() => {
    if (!selectedTemplate) return;
    const firstButton = normalizeTemplateButtons(selectedTemplate.buttons)[0];
    if (firstButton) {
      setCTA((prev) => ({
        label: getButtonText(firstButton) || prev.label,
        url: getButtonUrl(firstButton) || prev.url,
      }));
    } else {
      setCTA({ label: "", url: "" });
    }
  }, [selectedTemplate]);

  // Sync campaignData
  useEffect(() => {
    if (!selectedTemplate) return;

    const { variable_column_map, variables, data_to_personalize } = mappingsToPayload(variableMappings);

    // A freshly-uploaded file for this send wins; otherwise fall back to
    // whatever real media header the template itself declares — video and
    // document templates need their actual header_type in the outgoing
    // payload too, not just image, or Meta will reject the send.
    const templateHeaderType = selectedTemplate.header_type === "file" ? "document" : selectedTemplate.header_type;
    const headerType: CampaignContent["header_type"] = mediaFile
      ? mediaFile.type.startsWith("image/") ? "image" : mediaFile.type.startsWith("video/") ? "video" : "document"
      : ["image", "video", "document"].includes(templateHeaderType || "")
        ? (templateHeaderType as CampaignContent["header_type"])
        : "none";

    const headerValue = mediaPreviewUrl || selectedTemplate.header_content || singleCampaign?.content?.header_value || undefined;

    const campaignContent: CampaignContent = {
      template_id: selectedTemplate.id,
      template_name: selectedTemplate.name,
      variables,
      variable_column_map,
      data_to_personalize,
      personalize: data_to_personalize.length > 0,
      header_type: headerType,
      header_value: headerValue,
      message: selectedTemplate.originalContent,
      footer: selectedTemplate.footer || "Reply STOP to unsubscribe",
      buttons: applyCtaOverride(
        normalizeTemplateButtons(selectedTemplate.buttons || singleCampaign?.content?.buttons || []),
        cta,
      ),
    };

    setCampaignData((prev) => ({
      ...prev,
      content: campaignContent,
      messages: selectedTemplate.originalContent,
      link: cta.url || prev.link,
      mediaFile: mediaFile || prev.mediaFile,
      mediaFileUrl: mediaPreviewUrl || prev.mediaFileUrl,
    }));

    const isValid =
      !!selectedTemplate &&
      !(selectedTemplate.type === "Media + Text" && !mediaPreviewUrl && !selectedTemplate.header_content) &&
      !(cta.label && !cta.url);

    onValidation?.(isValid);
  }, [selectedTemplate, variableMappings, cta, mediaFile, mediaPreviewUrl]);

  useEffect(() => {
    return () => { if (mediaPreviewUrl?.startsWith("blob:")) URL.revokeObjectURL(mediaPreviewUrl); };
  }, [mediaPreviewUrl]);

  const handleMediaUpload = (file: File) => {
    if (mediaPreviewUrl?.startsWith("blob:")) URL.revokeObjectURL(mediaPreviewUrl);
    const newUrl = URL.createObjectURL(file);
    setMediaFile(file);
    setMediaPreviewUrl(newUrl);
    setCampaignData((prev) => ({
      ...prev,
      mediaFile: file,
      mediaFileUrl: newUrl,
      mediaFileName: file.name,
      content: prev.content
        ? {
            ...prev.content,
            header_type: file.type.startsWith("image/") ? "image" : file.type.startsWith("video/") ? "video" : "document",
            header_value: newUrl,
          }
        : undefined,
    }));
  };

  const handleMediaRemove = () => {
    if (mediaPreviewUrl?.startsWith("blob:")) URL.revokeObjectURL(mediaPreviewUrl);
    setMediaFile(null);
    setMediaPreviewUrl(null);
    setCampaignData((prev) => ({
      ...prev,
      mediaFile: null,
      mediaFileUrl: undefined,
      content: prev.content ? { ...prev.content, header_type: "none", header_value: undefined } : undefined,
    }));
  };

  const handleConvoSend = async (_message: string, _history: ConvoAIMessage[]): Promise<string> => {
    await new Promise((r) => setTimeout(r, 800));
    return "Got it! I've noted your request. Tweak the variables and hit Generate when ready.";
  };

  const handleLaunch = () => {
    if (onSubmit) {
      onSubmit({
        ...(campaignData.content && { content: campaignData.content }),
        messages: campaignData.messages,
        link: campaignData.link,
      });
    }

    setShowLaunch(true);
  };

  const handleConfirmLaunch = () => setShowLaunch(false);

  const savedHeadersStr = localStorage.getItem("csv_headers");
  const csvColumnsFromStorage: string[] = savedHeadersStr ? JSON.parse(savedHeadersStr) : csvColumns;
  const savedRowStr = localStorage.getItem("first_row_values");
  const csvRowFromStorage: (string | number | null)[] = savedRowStr ? JSON.parse(savedRowStr) : [];

  const legacyVars: TemplateVariables = {};
  variableMappings.forEach((m) => {
    // WhatsappMockUp expects numeric placeholders ({{1}}) under "variable_1", named ones ({{today}}) as-is
    const key = /^\d+$/.test(m.variableName) ? `variable_${m.variableName}` : m.variableName;

    if (m.source === "static") {
      legacyVars[key] = m.staticValue || "";
    } else if (m.csvColumn) {
      const colIndex = csvColumnsFromStorage.indexOf(m.csvColumn);
      const rowValue = colIndex !== -1 ? csvRowFromStorage[colIndex] : undefined;
      legacyVars[key] =
        rowValue !== undefined && rowValue !== null
          ? String(rowValue)
          : csvPreviewRow?.[m.csvColumn] || "";
    }
  });

  return (
    <div className="min-h-screen font-sans">
      {/* Mobile top bar */}
      <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 shadow-sm">
        <button onClick={() => setMobileSidebarOpen(true)} className="flex items-center gap-2 text-slate-700 dark:text-slate-300 font-semibold text-sm">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          Templates
        </button>
        {selectedTemplate && <span className="text-xs font-medium text-indigo-600 truncate max-w-[160px]">{selectedTemplate.name}</span>}
        <button onClick={() => setMobileTab((t) => (t === "editor" ? "preview" : "editor"))} className="text-xs font-semibold text-indigo-600">
          {mobileTab === "editor" ? "Preview" : "Edit"}
        </button>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:grid lg:grid-cols-[260px_1fr_300px] h-screen">
        <TemplateSidebar
          templates={frontendTemplates}
          wabaAccounts={wabaAccounts}
          selected={selectedTemplate}
          onSelect={setSelectedTemplate}
          loading={templateLoader}
        />

        <div className="overflow-hidden border-r border-slate-200 dark:border-slate-700">
          {selectedTemplate ? (
            <TemplateEditor
              uploadedFileName={uploadedFileName}
              template={selectedTemplate}
              mappings={variableMappings}
              onMappingsChange={setVariableMappings}
              csvColumns={csvColumns}
              csvPreviewRow={csvPreviewRow}
              cta={cta}
              onCTAChange={setCTA}
              onBack={onBack}
              onLaunch={handleLaunch}
              onMediaUpload={handleMediaUpload}
              onMediaRemove={handleMediaRemove}
              mediaFile={mediaFile}
              mediaPreviewUrl={mediaPreviewUrl}
              backendTemplates={backendTemplates}
              onTemplateCreated={fetchTemplates}
              wabaAccounts={wabaAccounts}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-slate-400 dark:text-slate-500 text-sm">
              {templateLoader ? "Loading templates…" : frontendTemplates.length === 0 ? "No approved templates available" : "Select a template to get started"}
            </div>
          )}
        </div>

        <RightPanel
          template={selectedTemplate}
          senderName={campaignData?.sender_name}
          vars={legacyVars}
          cta={cta}
          mediaFile={mediaFile}
          isConvoOpen={isConvoOpen}
          onToggleConvo={() => setIsConvoOpen((v) => !v)}
          onConvoSend={handleConvoSend}
        />
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden">
        {selectedTemplate ? (
          mobileTab === "editor" ? (
            <div className="bg-white dark:bg-gray-900">
              <TemplateEditor
                uploadedFileName={uploadedFileName}
                template={selectedTemplate}
                mappings={variableMappings}
                onMappingsChange={setVariableMappings}
                csvColumns={csvColumns}
                csvPreviewRow={csvPreviewRow}
                cta={cta}
                onCTAChange={setCTA}
                onBack={() => setSelectedTemplate(null)}
                onLaunch={handleLaunch}
                onMediaUpload={handleMediaUpload}
                mediaFile={mediaFile}
                onMediaRemove={handleMediaRemove}
                backendTemplates={backendTemplates}
                onTemplateCreated={fetchTemplates}
              />
            </div>
          ) : (
            <div className="flex flex-col items-center py-8 bg-slate-50 dark:bg-transparent min-h-screen gap-6">
              <WhatsappMockUp template={selectedTemplate} vars={legacyVars} campaignData={campaignData} senderName={campaignData?.sender_name} cta={cta} mediaFile={mediaFile} />
              <button onClick={() => setIsConvoOpen(true)} className="w-10 h-10 rounded-full bg-[#075E54] flex items-center justify-center shadow-lg">
                <img src={SwitchIcon} alt="SwitchIcon" />
              </button>
              {isConvoOpen && (
                <div className="fixed inset-0 z-50 bg-white dark:bg-gray-900">
                  <ConvoAIPanel isOpen={isConvoOpen} onClose={() => setIsConvoOpen(false)} onSend={handleConvoSend} />
                </div>
              )}
            </div>
          )
        ) : (
          <div className="p-4">
            <TemplateSidebar templates={frontendTemplates} selected={null} onSelect={setSelectedTemplate} loading={templateLoader} />
          </div>
        )}
      </div>




      {/* Launch Modal */}
      {showLaunch && selectedTemplate && (
        <LaunchModal
          template={selectedTemplate}
          vars={legacyVars}
          cta={cta}
          mediaFile={mediaFile}
          onClose={() => setShowLaunch(false)}
          onConfirm={handleConfirmLaunch}
        />
      )}
      <ScrollToTop />
    </div>
  );
}